import { useAemConfig } from '@as-react/common-components';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeGetHasConsentOnMarketingCookies } from '../redux/cookieConsent/selectors/CookieConsentSelector';
import { initBloomreach, identifyBloomreach, subscribeBloomreachToTracking } from '../util/bloomreach';
import { useOAuth } from './useOAuth';
/**
 * Hook that manages the Bloomreach Engagement SDK lifecycle.
 *
 * Responsibilities:
 * - Calls brweb.start() after marketing cookie consent is granted
 * - Identifies the customer (brweb.identify) on login or returning authenticated session
 * - Subscribes Bloomreach to the generic tracking emitter for business events
 *
 * Anonymize on logout is handled in useOAuth.logout() before the page redirect.
 *
 * Mount this once at app root (Bootstrap).
 * Use emitter.emit() from components to send vendor-agnostic events.
 */
export const useBloomreachTracking = (debug = false) => {
    const { isShopLanding, bloomreachConfig: config } = useAemConfig();
    const getHasMarketingConsent = useMemo(makeGetHasConsentOnMarketingCookies, []);
    const hasMarketingConsent = useSelector(getHasMarketingConsent);
    // SDK is only active when marketing consent is granted and not on shop landing pages
    const isActive = hasMarketingConsent && !isShopLanding;
    const { isLoggedIn, userInfo } = useOAuth();
    const customerUuid = userInfo === null || userInfo === void 0 ? void 0 : userInfo.sub;
    // Initialize the SDK, call brweb.start(), and subscribe to tracking events
    useEffect(() => {
        if (!config || !isActive) {
            return;
        }
        initBloomreach(config, debug);
        subscribeBloomreachToTracking();
    }, [config === null || config === void 0 ? void 0 : config.isEnabled, isActive, debug]);
    // Identify the customer on login or when returning with an active session
    useEffect(() => {
        if (!(config === null || config === void 0 ? void 0 : config.isEnabled) || !isActive || !isLoggedIn || !customerUuid) {
            return;
        }
        identifyBloomreach({ customer_uuid: customerUuid });
    }, [config === null || config === void 0 ? void 0 : config.isEnabled, isActive, isLoggedIn, customerUuid]);
};
